/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { widgetStyles, animations } from '../../utils/designTokens';
import {
  getSuggestionsCategoryLabel,
  getSuggestionsOptionLabel
} from '../../utils/hairstyleSuggestionsI18n';


const FILTER_OPTIONS = {
  men: {
    length: ['short', 'medium', 'long'],
    texture: ['straight', 'wavy', 'curly', 'coily'],
    vibe: ['clean', 'lived-in', 'classic-modern', 'statement']
  },
  women: {
    length: ['short', 'medium', 'long', 'extra-long'],
    texture: ['straight', 'wavy', 'curly', 'coily'],
    vibe: ['elegant', 'casual', 'edgy', 'romantic', 'professional']
  }
};

const FiltersBar = ({
  query,
  onQueryChange,
  filters,
  onFiltersChange,
  gender = 'men'
}) => {
  const { isDark } = useTheme();
  const { t } = useTranslation('admin');
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  // Update parent with debounced query
  useEffect(() => {
    if (debouncedQuery !== query) {
      onQueryChange(debouncedQuery);
    }
  }, [debouncedQuery, query, onQueryChange]);

  // Load filters from localStorage on mount
  useEffect(() => {
    const savedFilters = localStorage.getItem('hairstyle-filters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        onFiltersChange(parsed);
      } catch (error) {
        console.error('Failed to parse saved filters:', error);
      }
    }
  }, [onFiltersChange]);

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem('hairstyle-filters', JSON.stringify(filters));
  }, [filters]);

  // Sync filters with URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Update URL with current filters
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 0) {
        urlParams.set(key, values.join(','));
      } else {
        urlParams.delete(key);
      }
    });

    if (query) {
      urlParams.set('search', query);
    } else {
      urlParams.delete('search');
    }

    const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
    window.history.pushState({}, '', newUrl);
  }, [filters, query]);

  // Clear filters when gender changes
  useEffect(() => {
    onFiltersChange({ length: [], texture: [], vibe: [] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender]);

  // Load filters from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters = { length: [], texture: [], vibe: [] };
    
    Object.keys(FILTER_OPTIONS[gender]).forEach(key => {
      const param = urlParams.get(key);
      if (param) {
        urlFilters[key] = param.split(',');
      }
    });

    const urlQuery = urlParams.get('search') || '';
    if (urlQuery) {
      onQueryChange(urlQuery);
    }

    // Only update filters if URL has different values
    const hasUrlFilters = Object.values(urlFilters).some(arr => arr.length > 0);
    if (hasUrlFilters) {
      onFiltersChange(urlFilters);
    }
  }, [onQueryChange, onFiltersChange]);

  const toggleFilter = useCallback((category, value) => {
    const currentValues = filters[category];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    onFiltersChange({
      ...filters,
      [category]: newValues
    });
  }, [filters, onFiltersChange]);

  const clearAllFilters = useCallback(() => {
    onFiltersChange({ length: [], texture: [], vibe: [] });
    onQueryChange('');
  }, [onFiltersChange, onQueryChange]);

  const hasActiveFilters = Object.values(filters).some(arr => arr.length > 0) || query;

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          placeholder={t('admin.suggestions_search_placeholder')}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
          style={{ '--accent': '#7d6633' }}
        />
      </div>

      {/* Filter Chips */}
      <div className="space-y-3">
        {Object.entries(FILTER_OPTIONS[gender] || FILTER_OPTIONS.men).map(([category, options]) => (
          <div key={category}>
            <h4 className="text-sm font-medium mb-2 capitalize text-zinc-700 dark:text-zinc-300">
              {getSuggestionsCategoryLabel(category, t)}
            </h4>
            <div className="flex flex-wrap gap-2">
              {options.map((option) => {
                const isSelected = filters[category].includes(option);
                return (
                  <motion.button
                    key={option}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => toggleFilter(category, option)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:ring-offset-2 ${
                      isSelected
                        ? 'bg-[--accent] text-white focus:ring-offset-white dark:focus:ring-offset-zinc-900'
                        : 'bg-white/50 dark:bg-zinc-800/50 text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 border border-white/20 dark:border-white/10 focus:ring-offset-white dark:focus:ring-offset-zinc-900'
                    }`}
                    style={{ '--accent': '#7d6633' }}
                  >
                    {getSuggestionsOptionLabel(option, t)}
                  </motion.button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <div className="pt-2 border-t border-white/10 dark:border-white/10">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={clearAllFilters}
            className="text-sm font-medium transition-colors duration-200 hover:underline focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:ring-offset-2 rounded text-zinc-600 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-300 focus:ring-offset-white dark:focus:ring-offset-zinc-900"
            style={{ '--accent': '#7d6633' }}
          >
            {t('admin.suggestions_clear_filters')}
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default FiltersBar;
